/**
 * Utility functions and constants for the video package.
 */
import {markMinorConversions} from "@khan/analytics-learning-time";
import * as Cookies from "@khan/data-cookies";
import Log, {Errors} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";
import type {Location} from "@khan/urls";
import {scrollToElement} from "@khan/util-scrolling";

import {VideoPlayerState} from "./constants.ts";

const _FULLSCREEN_EVENT_NAMES = [
    "webkitfullscreenchange",
    "mozfullscreenchange",
    "msfullscreenchange",
    "fullscreenchange",
];

/**
 * Standardizes adding "fullscreenchange" listeners for browsers with prefixed
 * support.
 *
 * @param {Function} handler  The change handler to add.
 * @return {Void}
 */
export function addFullscreenChangeHandler(handler: (e: Event) => unknown) {
    _FULLSCREEN_EVENT_NAMES.forEach((name) =>
        document.addEventListener(name, handler),
    );
}

/**
 * Standardizes removing "fullscreenchange" listeners for browsers with
 * prefixed support.
 *
 * @param {Function} handler  The change handler to remove.
 * @return {Void}
 */
export function removeFullscreenChangeHandler(handler: (e: Event) => unknown) {
    _FULLSCREEN_EVENT_NAMES.forEach((name) =>
        document.removeEventListener(name, handler),
    );
}

/**
 * Standardizes `document.exitFullscreen` for browsers with prefixed support.
 *
 * @return {Void}
 */
export const exitFullscreen = () => {
    if (typeof document === "undefined") {
        return;
    }

    const prefixedMethodName = [
        "exitFullscreen",
        "mozCancelFullScreen",
        "webkitExitFullscreen",
    ];
    for (const methodName of prefixedMethodName) {
        const method = document[methodName];
        if (typeof method === "function") {
            // Some browser complain if you don't call method as a method on document.
            method.call(document);
            return;
        }
    }
};

type Player = {
    playVideo: () => void;
    seekTo: (seconds: number) => void;
};

/**
 * THIS FUNCTION IS AN ANTIPATTERN! Wherever possible, handle timestamp clicks
 * within a React component.
 *
 * Scan the entire page for video timestamps and turn them into clickable links
 * for the provided video player. It's a gross side-effect, but it's necessary
 * because a lot of our current systems export raw HTML.
 *
 * @param  {Object} player  A video player.
 * @return {Void}
 */
export const linkTimestampsToModel: (player: Player) => void = (() => {
    let _currentPlayer: Player;

    const navigateToTimestamp = (target: HTMLElement) => {
        const containsTimestamp =
            target.classList.contains("youTube") ||
            target.classList.contains("youTubeLink");
        if (!(_currentPlayer && containsTimestamp)) {
            return;
        }

        // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        const seconds = parseInt(target.dataset.seconds);
        _currentPlayer.playVideo();
        _currentPlayer.seekTo(seconds);

        const playerList = document.getElementsByClassName("ka-video-player");
        if (playerList.length) {
            // @ts-expect-error - TS2345 - Argument of type 'Element' is not assignable to parameter of type 'HTMLElement'.
            scrollToElement(playerList[0]);
        }
    };

    const body = typeof document !== "undefined" && document.body;

    body &&
        body.addEventListener("click", (e: MouseEvent) => {
            navigateToTimestamp(e.target as any);
        });

    body &&
        body.addEventListener("keyup", (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                navigateToTimestamp(e.target as any);
            }
        });

    body &&
        body.addEventListener("touchend", (e: TouchEvent) => {
            // this potentially fires a double event on some mobile devices
            // (probably all android devices). In testing, we were unable to
            // detect any performance issues with an older android device.
            // Tried deboucing with requestAnimationFrame, but this is not a
            // react component so we cannot use wonder blocks timing and we
            // don't have a good hook to clean up any residual callbacks.
            navigateToTimestamp(e.target as any);
        });

    return function linkTimestampsToModel(player: Player) {
        _currentPlayer = player;
    };
})();

/**
 * Convenience function to check if the video has been interacted with yet.
 *
 * @param  {Number}  state  A VideoPlayerState state.
 * @return {Boolean}
 */
export function isUnstartedOrCued(
    state: (typeof VideoPlayerState)[keyof typeof VideoPlayerState],
): boolean {
    return (
        state === VideoPlayerState.UNSTARTED ||
        state === VideoPlayerState.VIDEO_CUED
    );
}

/**
 * Is YouTube blocked from use.
 *
 * Lets us know whether YouTube is blocked by the `disable_youtube` query param or
 * if the remote country is China.
 *
 * This call is safe for use during SSR.
 *
 * @param {Location} location The location for which this is being determined.
 * This is used to obtain the query param.
 * @return {boolean} True, if YouTube is blocked; otherwise, false.
 */
export function isYouTubeBlocked(location: Location): boolean {
    const {queryParams} = location;
    return !!queryParams.disable_youtube || RequestInfo.remoteCountry === "CN";
}

const USE_FALLBACK_PLAYER_COOKIE = "useFallbackPlayer";
/**
 * Should we use the fallback player.
 *
 * Lets us know if we should use the fallback player or try using YouTube.
 * This combines `isYouTubeBlocked` with a check for the fallback player
 * cookie (set automatically if YouTube errors or is blocked).
 *
 * ⚠️ WARNING: This call is not SSR safe.
 * TODO(somewhatabstract): To make this SSR safe, the Cookies access must be
 * given by `CookieProvider` rather than directly imported.
 *
 * @param {Location} location The location for which this is being determined.
 * This is used to obtain the `disable_youtube` query param.
 * @return {boolean} True, if the fallback player should be used; otherwise,
 * false.
 */
export function shouldUseFallbackPlayer(
    location: Location,
): string | boolean | null | undefined {
    return (
        isYouTubeBlocked(location) ||
        Cookies.readCookie(USE_FALLBACK_PLAYER_COOKIE)
    );
}

/**
 * Persist use of fallback player.
 *
 * Sets a cookie so that the fallback player is the default video player.
 * This allows us to retain this option over sessions, as needed.
 *
 * ⚠️ WARNING: This call is not SSR safe.
 * TODO(somewhatabstract): To make this SSR safe, the Cookies access must be
 * given by `CookieProvider` rather than directly imported.
 */
export function markFallbackPlayerRequired() {
    if (!Cookies.readCookie(USE_FALLBACK_PLAYER_COOKIE)) {
        Cookies.createCookie(
            USE_FALLBACK_PLAYER_COOKIE,
            Cookies.CookieCategory.Functional,
            "1",
            365,
        );
    }
}

/**
 * Mark fallback player as optional.
 *
 * Erases the cookie so that we try using YouTube first and only fallback to
 * the fallback player after that.
 *
 * ⚠️ WARNING: This call is not SSR safe.
 * TODO(somewhatabstract): To make this SSR safe, the Cookies access must be
 * given by `CookieProvider` rather than directly imported.
 */
export function markFallbackPlayerOptional() {
    Cookies.eraseCookie(USE_FALLBACK_PLAYER_COOKIE);
}

/**
 * Load the YouTube iframe API and attach it to the `window` object, then call
 * a handler function.
 *
 * @param  {Function} onSuccess  Called after the YouTube API loads.
 * @param  {Function} onFailure  Called if the YouTube API fails to load.
 * @return {Void}
 */
export const requireYouTubeAPI: (
    onSuccess: () => unknown,
    onFailure: () => unknown,
) => undefined | (() => void) = (() => {
    // Possible states of our YouTube API while we wait for it to load.
    const apiStates = {
        INITIAL: Symbol("INITIAL"),
        REQUESTED: Symbol("REQUESTED"),
        FAILED: Symbol("FAILED"),
        LOADED: Symbol("LOADED"),
    } as const;
    let apiState =
        // @ts-expect-error - TS2339 - Property 'YT' does not exist on type 'Window & typeof globalThis'. | TS2339 - Property 'YT' does not exist on type 'Window & typeof globalThis'.
        typeof window !== "undefined" && window.YT && window.YT.loaded
            ? apiStates.LOADED
            : apiStates.INITIAL;

    // Lists to hold our handlers while the API loads.
    const handlers = {success: [], failure: []} as const;

    // Call a list of handlers once the API loads or fails.
    function triggerHandlers(type: "success" | "failure") {
        handlers[type].forEach((handler) => {
            if (typeof handler === "function") {
                // @ts-expect-error - TS2349 - This expression is not callable.
                handler();
            }
        });
        // @ts-expect-error - TS2540 - Cannot assign to 'failure' because it is a read-only property. | TS2540 - Cannot assign to 'success' because it is a read-only property.
        handlers[type] = [];
    }

    return function requireYouTubeAPI(
        onSuccess: () => unknown,
        onFailure: () => unknown,
    ): undefined | (() => void) {
        switch (apiState) {
            case apiStates.LOADED: {
                onSuccess();
                return;
            }
            case apiStates.FAILED: {
                onFailure();
                return;
            }
            case apiStates.REQUESTED:
                // If the API has already been requested, we don't need to
                // request it again.
                break;
            default:
                // Request the API.
                // If the API hasn't loaded after some time, trigger a failure.
                // TODO(somewhatabstract): Use WB timing
                // eslint-disable-next-line no-restricted-syntax
                const apiTimeoutFailsafe = window.setTimeout(() => {
                    // @ts-expect-error - TS2339 - Property 'YT' does not exist on type 'Window & typeof globalThis'. | TS2339 - Property 'YT' does not exist on type 'Window & typeof globalThis'.
                    if (window.YT && window.YT.loaded) {
                        // We don't know why, but sometimes we never get an answer
                        // from the YouTube api even though it did update the window
                        // object to say that it's loaded okay.
                        apiState = apiStates.LOADED;
                        triggerHandlers("success");
                        return;
                    }

                    apiState = apiStates.FAILED;
                    triggerHandlers("failure");
                }, 5000);

                // If we’ve already attached an `onYouTubeIframeAPIReady`
                // handler, we don't want to overwrite it. This shouldn’t
                // happen in our codebase, but we check anyway for safety.
                // @ts-expect-error - TS2339 - Property 'onYouTubeIframeAPIReady' does not exist on type 'Window & typeof globalThis'.
                const existingHandler = window.onYouTubeIframeAPIReady;

                // When the API *does* load, trigger our success handlers.
                const newHandler = function onYouTubeIframeAPIReady() {
                    if (typeof existingHandler === "function") {
                        existingHandler();
                    }

                    apiState = apiStates.LOADED;
                    // TODO(somewhatabstract): Use WB timing
                    // eslint-disable-next-line no-restricted-syntax
                    window.clearTimeout(apiTimeoutFailsafe);
                    triggerHandlers("success");
                };

                // Called by the YouTube API on load. Reference:
                // https://developers.google.com/youtube/iframe_api_reference
                // @ts-expect-error - TS2339 - Property 'onYouTubeIframeAPIReady' does not exist on type 'Window & typeof globalThis'.
                window.onYouTubeIframeAPIReady = newHandler;

                // Append a <script> tag to the document to load the API.
                const head = document.getElementsByTagName("head")[0];
                const script = document.createElement("script");
                script.src = "https://www.youtube.com/iframe_api";
                head.insertBefore(script, head.firstChild);

                apiState = apiStates.REQUESTED;
        }

        // @ts-expect-error - TS2339 - Property 'push' does not exist on type 'readonly []'.
        handlers.success.push(onSuccess);
        // @ts-expect-error - TS2339 - Property 'push' does not exist on type 'readonly []'.
        handlers.failure.push(onFailure);

        return function cancel() {
            // TODO(mdr): If there are no handlers left, should we abort the
            //     loading process?
            // @ts-expect-error - TS2540 - Cannot assign to 'success' because it is a read-only property.
            handlers.success = handlers.success.filter((h) => h !== onSuccess);
            // @ts-expect-error - TS2540 - Cannot assign to 'failure' because it is a read-only property.
            handlers.failure = handlers.failure.filter((h) => h !== onFailure);
        };
    };
})();

export function logYoutubeError(
    title: string,
    youtubeUrl: string,
    errorCode?: number,
    errorDescription?: string,
) {
    //Possible errors from: https://developers.google.com/youtube/iframe_api_reference#Events
    /*
     * 2 – The request contains an invalid parameter value. For example, this error occurs if you specify a video ID that does not have 11 characters, or if the video ID contains invalid characters, such as exclamation points or asterisks.
     * 5 – The requested content cannot be played in an HTML5 player or another error related to the HTML5 player has occurred.
     * 100 – The video requested was not found. This error occurs when a video has been removed (for any reason) or has been marked as private.
     * 101 – The owner of the requested video does not allow it to be played in embedded players.
     * 150 – This error is the same as 101. It's just a 101 error in disguise!
     */

    const errorReasons = {
        "2": "Invalid parameter",
        "5": "Content cannot be played",
        "100": "Video not found",
        "101": "Restricted",
        "150": "Restricted",
    } as const;

    let extras = {
        title,
        youtubeUrl,
    };

    if (errorCode) {
        extras = {
            ...extras,
            // @ts-expect-error - TS2322 - Type '{ errorCode: string; reason: any; title: string; youtubeUrl: string; }' is not assignable to type '{ title: string; youtubeUrl: string; }'.
            errorCode: errorCode.toString(),
            reason: errorReasons[errorCode] || "Unknown",
        };
    } else if (errorDescription) {
        // @ts-expect-error - TS2322 - Type '{ reason: string; title: string; youtubeUrl: string; }' is not assignable to type '{ title: string; youtubeUrl: string; }'.
        extras = {...extras, reason: errorDescription};
    }

    const message = "Failed to play Youtube video.  Using Fallback Player.";

    Log.error(message, Errors.Internal, {
        sentryData: {
            tags: {
                p1: "true",
            },
            contexts: {extras},
        },
    });

    markMinorConversions([
        {
            id: "youtube_failed_fallback_to_html",
            extra: extras,
        },
    ]);
}

/**
 * Detects if this document is loaded in the context of the mobile app. This
 * function uses some heuristics to do its best to detect that the native app
 * is the host.
 */
export const isInApp = (): boolean => {
    return (
        // Defined by the React Native webview for Web -> React Native comms
        // Defined in the mobile repo's `webview/javascript/environment-utils.js`
        window["ReactNativeWebView"] != null ||
        window["EnvironmentUtils"] != null
    );
};
